import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { DataSource } from '../data/DataSource'
import { cachedTransitionFields } from '../data/prefetchCache'
import type { FieldDef } from '../domain/types'

// ModalManager: registry central de modais.
//
// Antes esses 9 modais viviam como 9 useState no App.tsx (cancelFor, transferFor,
// rescheduleFor, detailFor, ticketCancelFor, transitionReq, confirmChangeScope,
// confirmLogout) + 3 useState para fields de formulário (cancelFields,
// transferFields, ticketCancelFields). Cada vez que qualquer slot mudava, o
// DashboardApp re-renderizava; cada setter espalhado pelo código (12+ callsites)
// era uma forma de mutar o estado direto sem type-safety. Centralizando:
//   - 1 único useReducer (3 fields paralelos viram derived state do slot)
//   - lazy-load de fields só quando o modal abre
//   - cleanup automático dos fields ao fechar (evita flicker com form anterior)
//   - type-safety: cada modal é um discriminated union, não 9 any
//
// O hook devolve { state, openCancelJourney, openTransfer, ... }: o componente
// consome a API, não os setters brutos. Trocar de useReducer para Zustand no
// futuro é trivial: só substituir o setState por set do store.

export interface TransitionRequest {
  ticketId: number
  title: string
  fields: FieldDef[]
  run: (f?: Record<string, string>) => Promise<void>
}

export interface ModalState {
  cancelFor: string | null
  transferFor: number | null
  rescheduleFor: number | null
  detailFor: number | null
  ticketCancelFor: number | null
  transitionReq: TransitionRequest | null
  transitioning: boolean
  confirmChangeScope: boolean
  confirmLogout: boolean
  cancelFields: FieldDef[]
  transferFields: FieldDef[]
  ticketCancelFields: FieldDef[]
}

export interface ModalActions {
  openCancelJourney: (aggId: string | null) => void
  openTransfer: (ticketId: number | null) => void
  openReschedule: (ticketId: number | null) => void
  openDetails: (ticketId: number | null) => void
  openTicketCancel: (ticketId: number | null) => void
  requestTransition: (req: TransitionRequest | null) => void
  setTransitioning: (v: boolean) => void
  setConfirmChangeScope: (v: boolean) => void
  setConfirmLogout: (v: boolean) => void
}

export type ModalManagerApi = ModalState & ModalActions

export interface ModalManagerDeps {
  ds: DataSource
  // Injetado para o lazy-load de OnCancel fields (precisa do aggregatorId corrente
  // para descobrir o templateId/customerId). O caller computa isso a partir do
  // aggs — manter fora do hook pra evitar ciclo de deps com data store.
  getCancelContext?: () => { templateId: number | null | undefined; customerId: number | null | undefined } | null
}

const EMPTY: ModalState = {
  cancelFor: null,
  transferFor: null,
  rescheduleFor: null,
  detailFor: null,
  ticketCancelFor: null,
  transitionReq: null,
  transitioning: false,
  confirmChangeScope: false,
  confirmLogout: false,
  cancelFields: [],
  transferFields: [],
  ticketCancelFields: [],
}

export function useModalManager(deps: ModalManagerDeps): ModalManagerApi {
  const [state, setState] = useState<ModalState>(EMPTY)

  // `deps` chega como objeto literal recriado a cada render do caller (ver App.tsx).
  // Mantê-lo no array de dependências fazia o fetch de campos rodar a cada render; e como
  // cada resposta gravava um objeto novo no state, o ciclo render → efeito → fetch →
  // setState → render nunca terminava: com o modal aberto o backend levava uma tempestade
  // de requisições. Agora:
  //   - `ds` (estável) é a única dep "de serviço" dos efeitos, junto do id do modal;
  //   - `getCancelContext` sai das deps e é lido de um ref atualizado em `useLayoutEffect`,
  //     que roda antes dos efeitos passivos do mesmo commit — o fetch sempre enxerga o
  //     callback do render corrente.
  const { ds } = deps
  const getCancelContextRef = useRef(deps.getCancelContext)
  useLayoutEffect(() => { getCancelContextRef.current = deps.getCancelContext })

  // Lazy-load: OnCancel fields para cancelJourney. Só dispara quando cancelFor muda
  // de null para um id. Cleanup via `alive` evita setState em unmount.
  useEffect(() => {
    if (state.cancelFor == null) {
      setState((s) => (s.cancelFields.length === 0 ? s : { ...s, cancelFields: [] }))
      return
    }
    const ctx = getCancelContextRef.current?.() ?? null
    if (!ds) { return }
    let alive = true
    ds
      .getJourneyFields(ctx?.templateId ?? null, ctx?.customerId ?? null, 'OnCancel')
      .then((f) => { if (alive) { setState((s) => ({ ...s, cancelFields: f })) } })
      .catch(() => { if (alive) { setState((s) => ({ ...s, cancelFields: [] })) } })
    return () => { alive = false }
  }, [state.cancelFor, ds])

  // Lazy-load: OnCancel fields para cancelTicket (per-ticket, não per-journey).
  useEffect(() => {
    if (state.ticketCancelFor == null) {
      setState((s) => (s.ticketCancelFields.length === 0 ? s : { ...s, ticketCancelFields: [] }))
      return
    }
    let alive = true
    cachedTransitionFields(ds, state.ticketCancelFor, 'OnCancel')
      .then((f) => { if (alive) { setState((s) => ({ ...s, ticketCancelFields: f })) } })
      .catch(() => { if (alive) { setState((s) => ({ ...s, ticketCancelFields: [] })) } })
    return () => { alive = false }
  }, [state.ticketCancelFor, ds])

  // Lazy-load: OnTransfer fields para transfer modal.
  useEffect(() => {
    if (state.transferFor == null) {
      setState((s) => (s.transferFields.length === 0 ? s : { ...s, transferFields: [] }))
      return
    }
    let alive = true
    cachedTransitionFields(ds, state.transferFor, 'OnTransfer')
      .then((f) => { if (alive) { setState((s) => ({ ...s, transferFields: f })) } })
      .catch(() => { if (alive) { setState((s) => ({ ...s, transferFields: [] })) } })
    return () => { alive = false }
  }, [state.transferFor, ds])

  // Setters com identidade estável: o App guarda vários deles dentro do useMemo de
  // `actions` (consumido por 6+ telas). Setter inline recriava o objeto a cada render.
  const openCancelJourney = useCallback((v: string | null) => setState((s) => ({ ...s, cancelFor: v })), [])
  const openTransfer = useCallback((v: number | null) => setState((s) => ({ ...s, transferFor: v })), [])
  const openReschedule = useCallback((v: number | null) => setState((s) => ({ ...s, rescheduleFor: v })), [])
  const openDetails = useCallback((v: number | null) => setState((s) => ({ ...s, detailFor: v })), [])
  const openTicketCancel = useCallback((v: number | null) => setState((s) => ({ ...s, ticketCancelFor: v })), [])
  const requestTransition = useCallback((v: TransitionRequest | null) => setState((s) => ({ ...s, transitionReq: v })), [])
  const setTransitioning = useCallback((v: boolean) => setState((s) => ({ ...s, transitioning: v })), [])
  const setConfirmChangeScope = useCallback((v: boolean) => setState((s) => ({ ...s, confirmChangeScope: v })), [])
  const setConfirmLogout = useCallback((v: boolean) => setState((s) => ({ ...s, confirmLogout: v })), [])

  return {
    ...state,
    openCancelJourney,
    openTransfer,
    openReschedule,
    openDetails,
    openTicketCancel,
    requestTransition,
    setTransitioning,
    setConfirmChangeScope,
    setConfirmLogout,
  }
}
